<template lang="pug">
div.root.mcp-admin-merchant-details-section(:class="{'is-loading':isLoading}")
    div.mcp-admin-merchant-details-section__header
        | Company Contacts
        ConfirmDataChange(
          data-testid="edit-btn"
          v-if="!readonly && !isEditMode"
          :confirmCb="onEditClick"
        )
        span.mcp-admin-merchant-detail__header-label(v-if="showDataChangedLabel" data-testid="data-change-label")
          | Data for review


    div.mcp-admin__spinner-container(data-testid="loading-indicator" v-if="isLoading")
        div.dc-spinner

    ContactsForm(
        data-testid="contacts-form"
        v-else-if="isEditMode"
        :is-submitting="isSubmitting"
        :initial-contacts="contacts"
        :has-zfs="hasZfs"
        @cancel="cancel"
        @save="onSave"
        :is-market="false"
        @form-changed="formChanged = true"
    )

    div(v-else-if="contacts.length == 0" data-testid="no-contacts") No contacts data available

    div(v-else)
        div.contact-group(v-for="cGroup in groupedContacts" data-testid="cgroup")
            div.dc-h4.ctype-label(data-testid="cgroup-title") {{ typeLabels[cGroup.ctype] }}
            div
              div.contact(v-for="contact in cGroup.items" data-testid="contact")
                div.contact-field
                  div.label First name
                  div {{ contact.firstName }}
                div.contact-field
                  div.label Last name
                  div {{ contact.lastName }}
                div.contact-field
                  div.label Email
                  div {{ contact.email }}
                div.contact-field
                  div.label Phone
                  div {{ contact.phone || '-' }}



</template>

<script lang="ts">
import Vue from 'vue';
import {
  fetchContacts,
  updateContacts,
} from 'Api/endpoints/merchant/merchant-contact.endpoint';
import ContactsForm from './company-details-contacts-form-container.vue';
import ConfirmDataChange from '@/common/components/confirm-data-change/confirm-data-change.vue';
import {
  CTYPE_LABELS,
  ZFS_CONTACT_TYPES,
  COMPANY_CONTACT_TYPES,
  CTYPE_GENERAL,
} from './company-details-contacts.constant';
import { USER_DID_NOT_CONFIRM_ERROR } from 'Common/constants/misc.constant';
import { groupBy, prop } from 'ramda';
import { sendUpdateMerchantDataReason } from '@/api/endpoints/merchant/merchant.endpoint.es';

export default Vue.extend({
  name: 'CompanyDetailsContactsContainer',
  components: {
    ContactsForm,
    ConfirmDataChange,
  },

  props: {
    merchantId: String,
    readonly: Boolean,
    getConfirm: Function, // required for non-readonly version only
    onSaveSuccess: Function, // required for non-readonly version only
    onSaveError: Function, // required for non-readonly version only
    showDataChangedLabel: Boolean,
    hasZfs: Boolean,
  },

  data() {
    return {
      isLoading: true,
      isEditMode: false,
      isSubmitting: false,
      contacts: [],
      typeLabels: CTYPE_LABELS,
      editReason: '',
      formChanged: false,
    };
  },

  computed: {
    groupedContacts(): unknown[] {
      // Group contacts by type and ensure the sorting is according CONTACT_TYPES
      const contactsByType = groupBy(prop('contactType'), this.contacts);

      const zfsContactTypes = (this.hasZfs && ZFS_CONTACT_TYPES) || [];

      const contactTypes = [...COMPANY_CONTACT_TYPES, ...zfsContactTypes];
      const companyMainContact = contactsByType[CTYPE_GENERAL];

      return contactTypes.map((ctype) => ({
        ctype,
        items: contactsByType[ctype] || companyMainContact || [],
      }));
    },
  },

  mounted() {
    fetchContacts(this.merchantId).then((items) => {
      this.contacts = items;
      this.isLoading = false;
    });
  },

  methods: {
    onEditClick(reason: string) {
      this.editReason = reason;
      this.isEditMode = true;
    },
    cancel(): void {
      this.isEditMode = false;
      this.editReason = '';
      this.formChanged = false;
    },
    onSave(contacts): void {
      if (this.formChanged) {
        this.isSubmitting = true;

        updateContacts(this.merchantId, contacts)
          .then((newContacts) => {
            this.contacts = newContacts;
            this.isEditMode = false;
            this.onSaveSuccess();
          })
          .catch((err) => {
            if (err !== USER_DID_NOT_CONFIRM_ERROR) {
              this.onSaveError();
            }
          })
          .finally(() => {
            this.isSubmitting = false;
          });

        if (this.editReason) {
          sendUpdateMerchantDataReason(this.merchantId, this.editReason);
        }
      } else {
        this.cancel();
      }
    },
  },
});
</script>

<style lang="scss" scoped>
.mcp-admin-merchant-details-section {
  border-bottom: 1px solid #d1d1d1;
}
.contact-group {
  margin-bottom: 18px;
}
.contact {
  margin-bottom: 10px;
}
.ctype-label {
  margin-top: 20px;
  margin-bottom: 10px;
}
.contact-field {
  display: flex;
}
.label {
  width: 145px;
  font-weight: 500;
  text-transform: uppercase;
  font-size: 11px;
}
.root.is-loading {
  min-height: 200px;
}
</style>
